import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

function AppRuntimeSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [notiColor, setNotiColor] = useState("success");
  const [form, setForm] = useState({
    razorpay_key_id: "",
    razorpay_key_secret: "",
    wallet_withdrawal_enabled: "true",
    has_razorpay_key_secret: false,
    practice_subjects_visible: "true",
    practice_topics_visible: "true",
    ar_progressive_unlock_enabled: "true",
    ar_initial_open_levels_count: "2",
    ar_unlock_required_accuracy_percent: "75",
    ar_unlock_next_level_price: "19",
    ar_unlock_all_levels_price: "99",
    ar_unlock_next_level_message: "",
    ar_unlock_all_levels_message: "",
    ar_unlock_currency: "INR",
    agriboost_daily_free_limit: "10",
    agriboost_reveal_time_seconds: "30",
    blogs_section_visible: "true",
    videos_section_visible: "true",
    pdfs_section_visible: "true",
  });

  const showNotice = (content, color = "success") => {
    setMessage(content);
    setNotiColor(color);
    setNotification(true);
    setTimeout(() => {
      setNotification(false);
      setMessage("");
      setNotiColor("success");
    }, 2500);
  };

  const loadSettings = async () => {
    try {
      if (init() !== "success") {
        window.location.assign("/signin");
        return;
      }

      setLoading(true);
      const response = await axios.get(Constants.getUrls.getAppRuntimeSettings);
      const data = response?.data?.data || {};
      setForm({
        razorpay_key_id: data?.razorpay_key_id || "",
        razorpay_key_secret: "",
        wallet_withdrawal_enabled: String(Boolean(data?.wallet_withdrawal_enabled)),
        has_razorpay_key_secret: Boolean(data?.has_razorpay_key_secret),
        practice_subjects_visible: String(Boolean(data?.practice_subjects_visible)),
        practice_topics_visible: String(Boolean(data?.practice_topics_visible)),
        ar_progressive_unlock_enabled: String(Boolean(data?.ar_progressive_unlock_enabled)),
        ar_initial_open_levels_count: String(data?.ar_initial_open_levels_count ?? 2),
        ar_unlock_required_accuracy_percent: String(
          data?.ar_unlock_required_accuracy_percent ?? 75
        ),
        ar_unlock_next_level_price: String(data?.ar_unlock_next_level_price ?? 19),
        ar_unlock_all_levels_price: String(data?.ar_unlock_all_levels_price ?? 99),
        ar_unlock_next_level_message: data?.ar_unlock_next_level_message || "",
        ar_unlock_all_levels_message: data?.ar_unlock_all_levels_message || "",
        ar_unlock_currency: data?.ar_unlock_currency || "INR",
        agriboost_daily_free_limit: String(data?.agriboost_daily_free_limit ?? 10),
        agriboost_reveal_time_seconds: String(data?.agriboost_reveal_time_seconds ?? 30),
        blogs_section_visible: String(Boolean(data?.blogs_section_visible)),
        videos_section_visible: String(Boolean(data?.videos_section_visible)),
        pdfs_section_visible: String(Boolean(data?.pdfs_section_visible)),
      });
    } catch (error) {
      showNotice(
        error?.response?.data?.message || "Unable to load app runtime settings.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      if (init() !== "success") {
        window.location.assign("/signin");
        return;
      }

      setSaving(true);
      const response = await axios.put(Constants.putUrls.updateAppRuntimeSettings, {
        razorpay_key_id: form.razorpay_key_id,
        razorpay_key_secret: form.razorpay_key_secret,
        wallet_withdrawal_enabled: form.wallet_withdrawal_enabled === "true",
        practice_subjects_visible: form.practice_subjects_visible === "true",
        practice_topics_visible: form.practice_topics_visible === "true",
        ar_progressive_unlock_enabled: form.ar_progressive_unlock_enabled === "true",
        ar_initial_open_levels_count: Number(form.ar_initial_open_levels_count || 0),
        ar_unlock_required_accuracy_percent: Number(
          form.ar_unlock_required_accuracy_percent || 0
        ),
        ar_unlock_next_level_price: Number(form.ar_unlock_next_level_price || 0),
        ar_unlock_all_levels_price: Number(form.ar_unlock_all_levels_price || 0),
        ar_unlock_next_level_message: form.ar_unlock_next_level_message,
        ar_unlock_all_levels_message: form.ar_unlock_all_levels_message,
        ar_unlock_currency: form.ar_unlock_currency,
        agriboost_daily_free_limit: Number(form.agriboost_daily_free_limit || 0),
        agriboost_reveal_time_seconds: Number(form.agriboost_reveal_time_seconds || 0),
        blogs_section_visible: form.blogs_section_visible === "true",
        videos_section_visible: form.videos_section_visible === "true",
        pdfs_section_visible: form.pdfs_section_visible === "true",
      });

      const data = response?.data?.data || {};
      setForm((prev) => ({
        ...prev,
        razorpay_key_secret: "",
        has_razorpay_key_secret: Boolean(data?.has_razorpay_key_secret),
        wallet_withdrawal_enabled: String(Boolean(data?.wallet_withdrawal_enabled)),
        practice_subjects_visible: String(Boolean(data?.practice_subjects_visible)),
        practice_topics_visible: String(Boolean(data?.practice_topics_visible)),
        ar_progressive_unlock_enabled: String(Boolean(data?.ar_progressive_unlock_enabled)),
        ar_initial_open_levels_count: String(data?.ar_initial_open_levels_count ?? 2),
        ar_unlock_required_accuracy_percent: String(
          data?.ar_unlock_required_accuracy_percent ?? 75
        ),
        ar_unlock_next_level_price: String(data?.ar_unlock_next_level_price ?? 19),
        ar_unlock_all_levels_price: String(data?.ar_unlock_all_levels_price ?? 99),
        ar_unlock_next_level_message: data?.ar_unlock_next_level_message || "",
        ar_unlock_all_levels_message: data?.ar_unlock_all_levels_message || "",
        ar_unlock_currency: data?.ar_unlock_currency || "INR",
        agriboost_daily_free_limit: String(data?.agriboost_daily_free_limit ?? 10),
        agriboost_reveal_time_seconds: String(data?.agriboost_reveal_time_seconds ?? 30),
        blogs_section_visible: String(Boolean(data?.blogs_section_visible)),
        videos_section_visible: String(Boolean(data?.videos_section_visible)),
        pdfs_section_visible: String(Boolean(data?.pdfs_section_visible)),
      }));
      showNotice(response?.data?.message || "App runtime settings updated successfully.");
    } catch (error) {
      showNotice(
        error?.response?.data?.message || "Unable to update app runtime settings.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              App Runtime Settings
            </MDTypography>
          </MDBox>

          <MDBox p={3}>
            {loading ? (
              <MDTypography variant="button" color="text">
                Loading settings...
              </MDTypography>
            ) : (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <InputLabel>Razorpay Key ID</InputLabel>
                    <TextField
                      fullWidth
                      value={form.razorpay_key_id}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          razorpay_key_id: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputLabel>Razorpay Secret</InputLabel>
                    <TextField
                      fullWidth
                      type="password"
                      value={form.razorpay_key_secret}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          razorpay_key_secret: event.target.value,
                        }))
                      }
                      helperText={
                        form.has_razorpay_key_secret
                          ? "Secret already saved. Leave blank to keep the current one."
                          : "Enter secret to save it in backend runtime settings."
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputLabel>Wallet Withdrawal</InputLabel>
                    <FormControl fullWidth>
                      <Select
                        value={form.wallet_withdrawal_enabled}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            wallet_withdrawal_enabled: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="true">Enabled</MenuItem>
                        <MenuItem value="false">Disabled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <MDTypography variant="caption" color="text">
                      The app receives only the Razorpay key id. The secret stays on the backend
                      and is used only for order creation and signature verification.
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12}>
                    <MDTypography variant="button" color="text" fontWeight="medium">
                      Practice Flow Controls
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputLabel>Practice Subject Screen</InputLabel>
                    <FormControl fullWidth>
                      <Select
                        value={form.practice_subjects_visible}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            practice_subjects_visible: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="true">Show</MenuItem>
                        <MenuItem value="false">Hide</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputLabel>Practice Topic Screen</InputLabel>
                    <FormControl fullWidth>
                      <Select
                        value={form.practice_topics_visible}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            practice_topics_visible: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="true">Show</MenuItem>
                        <MenuItem value="false">Hide</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <MDTypography variant="button" color="text" fontWeight="medium">
                      AR Section Controls
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InputLabel>AR Progressive Unlock</InputLabel>
                    <FormControl fullWidth>
                      <Select
                        value={form.ar_progressive_unlock_enabled}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            ar_progressive_unlock_enabled: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="true">Enabled</MenuItem>
                        <MenuItem value="false">Disabled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <InputLabel>Initial Open Levels</InputLabel>
                    <TextField
                      fullWidth
                      type="number"
                      value={form.ar_initial_open_levels_count}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          ar_initial_open_levels_count: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <InputLabel>Required Accuracy %</InputLabel>
                    <TextField
                      fullWidth
                      type="number"
                      value={form.ar_unlock_required_accuracy_percent}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          ar_unlock_required_accuracy_percent: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <InputLabel>Unlock Next Level Price</InputLabel>
                    <TextField
                      fullWidth
                      type="number"
                      value={form.ar_unlock_next_level_price}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          ar_unlock_next_level_price: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <InputLabel>Unlock All AR Levels Price</InputLabel>
                    <TextField
                      fullWidth
                      type="number"
                      value={form.ar_unlock_all_levels_price}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          ar_unlock_all_levels_price: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InputLabel>AR Unlock Currency</InputLabel>
                    <TextField
                      fullWidth
                      value={form.ar_unlock_currency}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          ar_unlock_currency: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputLabel>Unlock Next Level Message</InputLabel>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      value={form.ar_unlock_next_level_message}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          ar_unlock_next_level_message: event.target.value,
                        }))
                      }
                      helperText="Available tokens: {{next_level_name}}, {{next_level_price}}, {{required_accuracy_percent}}, {{currency_symbol}}, {{cumulative_accuracy_percent}}"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputLabel>Unlock All Levels Message</InputLabel>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      value={form.ar_unlock_all_levels_message}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          ar_unlock_all_levels_message: event.target.value,
                        }))
                      }
                      helperText="Available tokens: {{unlock_all_levels_price}}, {{currency_symbol}}, {{required_accuracy_percent}}, {{cumulative_accuracy_percent}}"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <MDTypography variant="button" color="text" fontWeight="medium">
                      AgriBoost Controls
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputLabel>Daily Free Cards Limit</InputLabel>
                    <TextField
                      fullWidth
                      type="number"
                      value={form.agriboost_daily_free_limit}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          agriboost_daily_free_limit: event.target.value,
                        }))
                      }
                      helperText="How many AgriBoost cards one user can reveal per day."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputLabel>Reveal Timer (Seconds)</InputLabel>
                    <TextField
                      fullWidth
                      type="number"
                      value={form.agriboost_reveal_time_seconds}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          agriboost_reveal_time_seconds: event.target.value,
                        }))
                      }
                      helperText="Controls answer auto-reveal time for AgriBoost cards."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <MDTypography variant="button" color="text" fontWeight="medium">
                      Resources Screen Controls
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InputLabel>Blogs Tab</InputLabel>
                    <FormControl fullWidth>
                      <Select
                        value={form.blogs_section_visible}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            blogs_section_visible: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="true">Show</MenuItem>
                        <MenuItem value="false">Hide</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InputLabel>Videos Tab</InputLabel>
                    <FormControl fullWidth>
                      <Select
                        value={form.videos_section_visible}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            videos_section_visible: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="true">Show</MenuItem>
                        <MenuItem value="false">Hide</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InputLabel>PDFs Tab</InputLabel>
                    <FormControl fullWidth>
                      <Select
                        value={form.pdfs_section_visible}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            pdfs_section_visible: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="true">Show</MenuItem>
                        <MenuItem value="false">Hide</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <MDBox mt={3}>
                  <MDButton type="submit" variant="contained" color="info" disabled={saving}>
                    {saving ? "Saving..." : "Save Settings"}
                  </MDButton>
                </MDBox>
              </form>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <MDSnackbar
        color={notiColor}
        icon="notifications"
        title="AgriQuiz"
        content={message}
        dateTime=""
        open={notification}
        onClose={() => setNotification(false)}
        close={() => setNotification(false)}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default AppRuntimeSettings;
